import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Snackbar, 
  Alert,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const NotificationContext = createContext();

/**
 * Get user-friendly error title from error code
 * @param {string} code - Error code from backend
 * @returns {string} User-friendly error title
 */
const getErrorTitle = (code) => {
  const errorTitles = {
    VALIDATION_ERROR: 'Validation Error',
    AUTHENTICATION_ERROR: 'Authentication Required',
    AUTHORIZATION_ERROR: 'Permission Denied',
    NOT_FOUND: 'Not Found',
    CONFLICT_ERROR: 'Conflict',
    PAYMENT_ERROR: 'Payment Failed',
    DATABASE_ERROR: 'System Error',
    EXTERNAL_SERVICE_ERROR: 'Service Unavailable',
    RATE_LIMIT_ERROR: 'Too Many Requests',
    BAD_REQUEST: 'Invalid Request',
    INTERNAL_ERROR: 'Server Error',
    INVALID_CREDENTIALS: 'Invalid Credentials',
    CHAT_LOCKED: 'Chat Locked'
  };

  return errorTitles[code] || 'Error';
};

/**
 * Hook to use the notification context
 * @returns {Object} Notification context methods and state
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Provider component for notification context
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  /**
   * Add a new notification
   * @param {Object} notification Notification data
   * @param {string} notification.type Type of notification ('success', 'error', 'info', 'warning')
   * @param {string} notification.message Message to display
   * @param {string} notification.title Optional title
   * @param {number} notification.duration Duration in ms, defaults to 6000ms
   */
  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [
      ...prev,
      {
        id,
        type: notification.type || 'info',
        message: notification.message,
        title: notification.title,
        severity: notification.severity || 'info',
        duration: notification.duration || 6000
      }
    ]);

    // Auto-remove after duration
    if (notification.duration !== 0) {
      setTimeout(() => {
        removeNotification(id);
      }, notification.duration || 6000);
    }

    return id;
  }, []);

  /**
   * Remove a notification by ID
   * @param {number} id The notification ID to remove
   */
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  /**
   * Remove all notifications
   */
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  /**
   * Show error notification from API error response
   * Handles the standardized error format: { success: false, error: { code, message, details } }
   * @param {Object|Error|string} error - Error object from API or Error instance or error message string
   * @param {string} fallbackMessage - Fallback message if error parsing fails
   */
  const showError = useCallback((error, fallbackMessage = 'An error occurred') => {
    let message = fallbackMessage;
    let title = 'Error';
    let details = null;

    // Handle different error formats
    if (typeof error === 'string') {
      message = error;
    } else if (error?.response?.data) {
      // Axios error response with our standardized format
      const errorData = error.response.data;

      if (errorData.error) {
        message = errorData.error.message || fallbackMessage;
        title = getErrorTitle(errorData.error.code);
        details = errorData.error.details;
      } else if (errorData.message) {
        message = errorData.message;
      }
    } else if (error?.message) {
      // Error object or generic error
      message = error.message;
    }

    // Format details if present
    if (details && Object.keys(details).length > 0) {
      const detailsText = Object.entries(details)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
      message = `${message} (${detailsText})`;
    }

    addNotification({
      type: 'error',
      severity: 'error',
      title,
      message,
      duration: 8000 // Errors stay longer
    });
  }, [addNotification]);

  /**
   * Show success notification
   * @param {string} message - Success message
   * @param {string} title - Optional title
   */
  const showSuccess = useCallback((message, title = 'Success') => {
    addNotification({
      type: 'success',
      severity: 'success',
      title,
      message,
      duration: 4000
    });
  }, [addNotification]);

  /**
   * Show info notification
   * @param {string} message - Info message
   * @param {string} title - Optional title
   */
  const showInfo = useCallback((message, title = 'Info') => {
    addNotification({
      type: 'info',
      severity: 'info',
      title,
      message,
      duration: 5000
    });
  }, [addNotification]);

  /**
   * Show warning notification
   * @param {string} message - Warning message
   * @param {string} title - Optional title
   */
  const showWarning = useCallback((message, title = 'Warning') => {
    addNotification({
      type: 'warning',
      severity: 'warning',
      title,
      message,
      duration: 6000
    });
  }, [addNotification]);

  // Render the actual notifications as Snackbars
  const notificationElements = notifications.map((notification) => (
    <Snackbar
      key={notification.id}
      open={true}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert 
        severity={notification.severity}
        sx={{ minWidth: '300px', width: '100%' }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => removeNotification(notification.id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        {notification.title && (
          <Typography variant="subtitle2" component="div" fontWeight="bold">
            {notification.title}
          </Typography>
        )}
        <Box sx={{ mt: notification.title ? 0.5 : 0 }}>
          {notification.message}
        </Box>
      </Alert>
    </Snackbar>
  ));

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        showError,
        showSuccess,
        showInfo,
        showWarning
      }}
    >
      {children}
      {notificationElements}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider; 